import { accountKeys, ApiProblemError, type AccountClass, contactKeys, entryKeys } from '@taler/api-client'
import { systemClock, todayUtc } from '@taler/domain'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, provide } from 'vue'
import { useApi } from '@/app/api'
import { CONTACT_CREATOR_KEY, type ContactCreator } from '../internal/contact-creator.ts'
import type { FlowKind } from '../internal/draft.ts'
import {
  accountTargets,
  computeFrecency,
  contactTargets,
  type Frecency,
  type Target,
} from '../internal/targets.ts'

/** Wide enough to give frecency a real sample, within the mock's own page-size ceiling. */
const FRECENCY_SAMPLE_SIZE = 100

export interface UseEntryTargetsOptions {
  readonly ledgerId: () => string | undefined
  readonly locale: () => 'en' | 'hu'
  readonly enabled: () => boolean
  /** An account a module decides the meaning of: offered in the flow grid even when it is a
   * system account, never in the "Other" tab, where no module is consulted. */
  readonly owns?: (target: Target) => boolean
  /** Lets descendant account pickers offer creating a contact inline. */
  readonly canCreateContact?: () => boolean
}

export interface UseEntryTargets {
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  /** Every account leaf plus every contact-backing account, as one flat list of targets -
   * callers narrow by class and rank by frecency themselves (see `internal/targets.ts`). */
  readonly moneyTargets: ComputedRef<readonly Target[]>
  /** Accounts of the given classes, plus every contact - the counter side of a posting
   * always offers "a category or a contact" as one namespace (AGENTS.md's captain note). */
  readonly counterTargets: (classes: readonly AccountClass[]) => readonly Target[]
  /** Every account of every class, for the "Other" tab - minus system accounts and any a module owns. */
  readonly allTargets: ComputedRef<readonly Target[]>
  /** Every target a chosen id can resolve to, offered or not. */
  readonly targetsById: ComputedRef<ReadonlyMap<string, Target>>
  readonly frecency: ComputedRef<ReadonlyMap<string, Frecency>>
}

export const MONEY_CLASSES: readonly AccountClass[] = ['ASSET', 'LIABILITY']
const ALL_CLASSES: readonly AccountClass[] = ['ASSET', 'LIABILITY', 'EXPENSE', 'INCOME', 'EQUITY']

/** The counter side's class scope per kind - expense/income stay in their own category
 * class, a transfer's counter is another money account. Contacts join every scope (see
 * {@link UseEntryTargets.counterTargets}). */
export const COUNTER_CLASSES_BY_KIND: Readonly<Record<FlowKind, readonly AccountClass[]>> = {
  EXPENSE: ['EXPENSE'],
  INCOME: ['INCOME'],
  TRANSFER: MONEY_CLASSES,
}

export function useEntryTargets(options: UseEntryTargetsOptions): UseEntryTargets {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const accountsQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const contactsQuery = useQuery({
    queryKey: computed(() => contactKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.contacts.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const recentQuery = useQuery({
    queryKey: computed(() => entryKeys.list(options.ledgerId() ?? '', { limit: FRECENCY_SAMPLE_SIZE })),
    queryFn: ({ signal }) => api.entries.list(options.ledgerId()!, { limit: FRECENCY_SAMPLE_SIZE }, signal),
    enabled: active,
    retry: false,
  })

  const queryClient = useQueryClient()
  const contactCreator: ContactCreator = {
    enabled: () => active.value && options.canCreateContact?.() === true,
    async create(input) {
      const ledgerId = options.ledgerId()
      if (ledgerId === undefined) return { ok: false, detail: undefined }
      try {
        const contact = await api.contacts.create(ledgerId, {
          name: input.name,
          ...(input.notes === undefined || input.notes === '' ? {} : { notes: input.notes }),
        })
        await Promise.all(
          contactKeys
            .invalidatedByWrite(ledgerId)
            .map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        )
        return { ok: true, accountId: contact.accountId, name: contact.name }
      } catch (error) {
        return { ok: false, detail: error instanceof ApiProblemError ? error.problem.detail : undefined }
      }
    },
  }
  provide(CONTACT_CREATOR_KEY, contactCreator)

  const locale = computed(() => options.locale())
  const contactOptions = computed(() => contactTargets(contactsQuery.data.value ?? []))
  const contactNames = computed(
    () => new Map((contactsQuery.data.value ?? []).map((contact) => [contact.accountId, contact.name])),
  )
  // Never a contact: the account you spend or receive from must be a real money account
  // you personally hold - a contact is always the counter side of a posting, not this one.
  const owns = (target: Target): boolean => options.owns?.(target) === true
  const moneyTargets = computed(() =>
    accountTargets(accountsQuery.data.value ?? [], MONEY_CLASSES, locale.value, owns, contactNames.value),
  )
  const allTargets = computed(() => [
    ...accountTargets(
      accountsQuery.data.value ?? [],
      ALL_CLASSES,
      locale.value,
      undefined,
      contactNames.value,
    ).filter((target) => !owns(target)),
    ...contactOptions.value,
  ])
  const targetsById = computed(
    () =>
      new Map(
        [
          ...accountTargets(
            accountsQuery.data.value ?? [],
            ALL_CLASSES,
            locale.value,
            () => true,
            contactNames.value,
          ),
          ...contactOptions.value,
        ].map((target) => [target.id, target]),
      ),
  )

  return {
    isLoading: computed(
      () =>
        active.value &&
        (accountsQuery.isPending.value || contactsQuery.isPending.value || recentQuery.isPending.value),
    ),
    error: computed(() => accountsQuery.error.value ?? contactsQuery.error.value ?? recentQuery.error.value),
    moneyTargets,
    counterTargets: (classes) => [
      ...accountTargets(accountsQuery.data.value ?? [], classes, locale.value, owns, contactNames.value),
      ...contactOptions.value,
    ],
    allTargets,
    targetsById,
    frecency: computed(() => computeFrecency(recentQuery.data.value?.items ?? [], todayUtc(systemClock))),
  }
}
